/**
 * Thunk to refresh files from the API.
 * Called when a file-list P2P message arrives to get the latest file list from the server.
 */
import { createAppAsyncThunk } from "@/store/thunks/createAppAsyncThunk";
import { mapSixFilesDTOToSixFiles, mapSixFoldersDTOToSixFolders } from "@/store/mappers/mapSixDetail";
import { syncFilesFromApi } from "@/store/thunks/sixDetail/syncFilesThunk";

/**
 * Refreshes the file list for a Six from the API.
 * This is triggered when a file-list P2P message arrives, using the API as the source of truth.
 *
 * @param sixId - The Six ID to refresh files for
 */
export const refreshFilesFromApi = createAppAsyncThunk(
  "sixDetail/refreshFiles",
  async(sixId: string, { dispatch, getState, extra }) => {
    const log = extra.loggerService.createLogger({ namespace: 'six3ro:six:detail' });
    // Only refresh if we're viewing this Six
    const currentSixId = getState().sixDetail.sixId;
    if (currentSixId !== sixId) {
      log.debug(`Skipping refresh - viewing different Six (current: ${currentSixId}, message: ${sixId})`);
      return { sixId, files: [], folders: [], skipped: true };
    }

    log.info(`Refreshing files for sixId: ${sixId}`);

    const filesResponse = await extra.sixDetailService.fetchFiles(sixId);
    const files = mapSixFilesDTOToSixFiles(filesResponse.files);
    const folders = mapSixFoldersDTOToSixFolders(filesResponse.folders);

    // Trigger local Helia sync after refresh.
    // Pass files directly — same race condition as initSixDetail: the fulfilled
    // reducer hasn't stored them yet when this dispatch runs.
    void dispatch(syncFilesFromApi({ sixId, files }));

    return { sixId, files, folders, skipped: false };
  }
);
